import { getTrinidadDayEnd, getTrinidadDayStart } from '@neighbourhood-munchies/shared-utils';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';

/**
 * Custom hook to fetch today's live orders count with auto-refresh
 * Live orders are any orders from today (Trinidad timezone) not marked as complete (delivered or cancelled)
 * Includes: Pending, Confirmed, Placed, and Dispatched
 */
export const usePendingOrdersCount = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['pending-orders-count'],
    queryFn: async () => {
      try {
        // Get today's date range in Trinidad timezone
        const startDate = getTrinidadDayStart().split('T')[0]; // YYYY-MM-DD format
        const endDate = getTrinidadDayEnd().split('T')[0]; // YYYY-MM-DD format

        // Fetch today's orders with date filtering
        const response = await apiClient.orders.getAll({
          limit: 1000,
          start_date: startDate,
          end_date: endDate,
        });

        const orders = response.data || [];

        // Count live orders (not complete)
        // Complete orders are: delivered, cancelled
        const liveStatuses = ['pending', 'confirmed', 'placed', 'dispatched'];
        const liveOrdersCount = orders.filter((order) =>
          liveStatuses.includes(order.order_status)
        ).length;

        return liveOrdersCount;
      } catch (err) {
        console.error('Failed to fetch live orders count:', err);
        throw err;
      }
    },
    // Auto-refresh every 30 seconds
    refetchInterval: 30000,
    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,
    // Retry on failure
    retry: 2,
    // Stale time of 25 seconds (slightly less than refetch interval)
    staleTime: 25000,
  });

  return {
    count: data ?? 0,
    isLoading,
    isError,
    error: error as Error | null,
  };
};
